import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Bug, Search, Sparkles, CheckCircle, User, ChevronRight, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { errorLogsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const severityColors = {
  critical: 'bg-red-600 text-white',
  error: 'bg-red-100 text-red-800',
  warning: 'bg-yellow-100 text-yellow-800',
  info: 'bg-blue-100 text-blue-800',
};

const statusColors = {
  new: 'bg-red-100 text-red-800',
  in_review: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  wont_fix: 'bg-gray-100 text-gray-800',
  duplicate: 'bg-purple-100 text-purple-800',
};

export default function ErrorLogsPage() {
  const { user } = useAuth();
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Resolve form
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolveStatus, setResolveStatus] = useState('resolved');

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        severity: severityFilter !== 'all' ? severityFilter : undefined,
        search: search || undefined,
        page,
        limit: 50,
      };
      const { data } = await errorLogsAPI.getAll(params);
      setErrors(data.errors);
      setTotal(data.total);
    } catch (error) {
      toast.error('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, search, page]);

  const fetchDashboard = async () => {
    try {
      const { data } = await errorLogsAPI.getDashboard('week');
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchErrors();
  };

  const viewErrorDetail = async (error) => {
    try {
      const { data } = await errorLogsAPI.getById(error.id);
      setSelectedError(data);
      setSheetOpen(true);
    } catch (error) {
      toast.error('Failed to load error details');
    }
  };

  const handleAssignToMe = async (errorId) => {
    try {
      await errorLogsAPI.assign(errorId, user.id);
      toast.success('Assigned to you');
      fetchErrors();
      if (selectedError?.id === errorId) {
        viewErrorDetail({ id: errorId });
      }
    } catch (error) {
      toast.error('Failed to assign error');
    }
  };

  const handleAnalyzeWithAI = async (errorId) => {
    setAnalyzing(true);
    try {
      const { data } = await errorLogsAPI.analyzeWithAI(errorId);
      toast.success('AI analysis complete');
      if (selectedError?.id === errorId) {
        setSelectedError(prev => ({ ...prev, ai_suggestion: data.suggestion }));
      }
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedError) return;
    try {
      await errorLogsAPI.resolve(selectedError.id, {
        resolution_notes: resolutionNotes,
        status: resolveStatus,
      });
      toast.success('Error resolved');
      setResolveDialogOpen(false);
      setResolutionNotes('');
      setSheetOpen(false);
      fetchErrors();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  const handleAIFeedback = async (errorId, wasHelpful) => {
    try {
      await errorLogsAPI.submitAIFeedback(errorId, { was_helpful: wasHelpful });
      toast.success('Feedback submitted');
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bug className="h-6 w-6" />
            Error Logs
          </h1>
          <p className="text-muted-foreground">Monitor and resolve system errors with AI assistance</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total_errors}</div>
              <p className="text-sm text-muted-foreground">Total Errors (7 days)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.unresolved}</div>
              <p className="text-sm text-muted-foreground">Unresolved</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600">{stats.critical_unresolved}</div>
              <p className="text-sm text-muted-foreground">Critical Unresolved</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{Math.round(stats.resolution_rate * 100)}%</div>
              <p className="text-sm text-muted-foreground">Resolution Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.trending_errors?.length || 0}</div>
              <p className="text-sm text-muted-foreground">Trending Issues</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="wont_fix">Won&apos;t Fix</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search errors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="max-w-xs"
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : errors.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
              <CheckCircle className="h-16 w-16 mb-4 text-green-500" />
              <p className="text-lg">No errors found</p>
              <p className="text-sm">System is running smoothly!</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {errors.map((error) => (
                  <div
                    key={error.id}
                    className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={severityColors[error.severity]}>
                            {error.severity}
                          </Badge>
                          <Badge className={statusColors[error.status]}>
                            {error.status.replace('_', ' ')}
                          </Badge>
                          {error.user_reported && (
                            <Badge variant="outline">User Reported</Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            x{error.occurrence_count}
                          </span>
                        </div>
                        <p className="font-medium text-sm">{error.error_type}</p>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {error.error_message}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>{error.module}</span>
                          <span>{error.endpoint_or_page}</span>
                          <span>{format(new Date(error.timestamp), 'MMM d, HH:mm')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {error.status === 'new' && (
                          <Button variant="outline" size="sm" onClick={() => handleAssignToMe(error.id)}>
                            <User className="h-4 w-4 mr-1" />
                            Assign to me
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => viewErrorDetail(error)}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {errors.length} of {total} errors
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={errors.length < 50} onClick={() => setPage(p => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Error Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[600px] sm:max-w-[600px]">
          <SheetHeader>
            <SheetTitle>Error Detail</SheetTitle>
          </SheetHeader>
          {selectedError && (
            <ScrollArea className="h-[calc(100vh-100px)] mt-4">
              <div className="space-y-6 pr-4">
                <div className="flex items-center gap-2">
                  <Badge className={severityColors[selectedError.severity]}>
                    {selectedError.severity}
                  </Badge>
                  <Badge className={statusColors[selectedError.status]}>
                    {selectedError.status.replace('_', ' ')}
                  </Badge>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Error Type</p>
                  <p className="font-mono text-sm">{selectedError.error_type}</p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Message</p>
                  <p className="text-sm bg-red-50 p-3 rounded border border-red-200">
                    {selectedError.error_message}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Module</p>
                    <p className="text-sm">{selectedError.module}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Endpoint</p>
                    <p className="text-sm font-mono">{selectedError.endpoint_or_page}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Occurrences</p>
                    <p className="text-sm">{selectedError.occurrence_count} times</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">First Seen</p>
                    <p className="text-sm">{format(new Date(selectedError.first_seen), 'PPp')}</p>
                  </div>
                </div>

                {selectedError.stack_trace && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Stack Trace</p>
                    <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-40 font-mono">
                      {selectedError.stack_trace}
                    </pre>
                  </div>
                )}

                {/* AI Analysis Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      AI Analysis
                    </h4>
                    {!selectedError.ai_suggestion && (
                      <Button size="sm" onClick={() => handleAnalyzeWithAI(selectedError.id)} disabled={analyzing}>
                        {analyzing ? (
                          <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Analyzing...</>
                        ) : (
                          <><Sparkles className="h-4 w-4 mr-2" /> Analyze with AI</>
                        )}
                      </Button>
                    )}
                  </div>

                  {selectedError.ai_suggestion ? (
                    <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                      <div>
                        <p className="text-xs font-medium text-purple-700 mb-1">Summary</p>
                        <p className="text-sm">{selectedError.ai_suggestion.analysis_summary}</p>
                      </div>

                      {selectedError.ai_suggestion.probable_causes?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-purple-700 mb-2">Probable Causes</p>
                          <div className="space-y-2">
                            {selectedError.ai_suggestion.probable_causes.map((cause, i) => (
                              <div key={i} className="bg-white p-2 rounded text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{cause.cause}</span>
                                  <Badge variant="outline">{Math.round(cause.confidence * 100)}%</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{cause.explanation}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedError.ai_suggestion.recommended_fixes?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-purple-700 mb-2">Recommended Fixes</p>
                          <div className="space-y-2">
                            {selectedError.ai_suggestion.recommended_fixes.map((fix, i) => (
                              <div key={i} className="bg-white p-2 rounded text-sm">
                                <p className="font-medium">{fix.fix}</p>
                                <div className="flex gap-2 mt-1">
                                  <Badge variant="outline">{fix.priority}</Badge>
                                  <Badge variant="outline">{fix.effort}</Badge>
                                </div>
                                {fix.code_suggestion && (
                                  <pre className="bg-muted p-2 rounded text-xs mt-2 overflow-auto">
                                    {fix.code_suggestion}
                                  </pre>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground">Was this helpful?</p>
                        <Button variant="ghost" size="sm" onClick={() => handleAIFeedback(selectedError.id, true)}>
                          <ThumbsUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleAIFeedback(selectedError.id, false)}>
                          <ThumbsDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Click &quot;Analyze with AI&quot; to get suggestions</p>
                  )}
                </div>

                {/* Actions */}
                {selectedError.status !== 'resolved' && (
                  <div className="border-t pt-4 flex gap-2">
                    {selectedError.status === 'new' && (
                      <Button variant="outline" onClick={() => handleAssignToMe(selectedError.id)}>
                        <User className="h-4 w-4 mr-2" /> Assign to me
                      </Button>
                    )}
                    <Button onClick={() => setResolveDialogOpen(true)}>
                      <CheckCircle className="h-4 w-4 mr-2" /> Resolve
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Error</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={resolveStatus} onValueChange={setResolveStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="wont_fix">Won&apos;t Fix</SelectItem>
                  <SelectItem value="duplicate">Duplicate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Resolution Notes</label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe how you resolved this issue..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleResolve}>Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
